import { Icon } from '@design/components/icon/Icon';
import { SvgIconNames } from '@design/design-tokens/iconography/SvgIcons';
import clsx from 'clsx';
import React, {
  HTMLAttributes,
  JSX,
  MouseEvent,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './Accordion.module.scss';

/** Shape of a single row passed to `<Accordion items>`. */
export interface AccordionItem {
  /** Stable id for the item. Auto-generated from `performance.now()` if omitted. */
  id?: string;
  /** Optional icon rendered to the left of the title (overrides the `variant="secondary"` default). */
  titleIcon?: SvgIconNames;
  /** Title content shown in the always-visible row. */
  title: ReactNode;
  /** Body content revealed when the row is expanded. Items without `content` are not expandable. */
  content?: ReactNode;
  /** Element rendered on the right side of the title row (alongside the chevron). */
  rightAlignedChild?: ReactNode;
  /** Override the default chevron with a custom icon. Receives the current expanded state. */
  renderIcon?: (expanded: boolean) => JSX.Element;
  /** Hide the chevron entirely (CSS controlled — interaction is unaffected). */
  hideIcon?: boolean;
  /** Extra class for this item's wrapper. */
  className?: string;
  /** Extra HTML attributes spread onto the item's wrapper `<div>`. */
  props?: HTMLAttributes<HTMLDivElement>;
  /** Optional value rendered alongside the right-aligned children when `showHasValue` is true and `value` is truthy. */
  value?: ReactNode;
  /** When true and `value` is set, render `value` next to the right-aligned children. */
  showHasValue?: boolean;
  /** When true, this item is expanded on initial render. Use this (not `defaultExpandedIds`) to set initial state. */
  expandedByDefault?: boolean;
  /** Pin a minimum height (px) for the expanded content. Used as the height when set, instead of measured `clientHeight`. */
  minHeight?: number;
  /** When true with `variant="secondary"`, the default leading `StarEmpty` is not rendered (compose icons in `title` instead). */
  hideDefaultTitleIcon?: boolean;
  /** Extra content rendered after the item's content block (outside the collapse animation). */
  footer?: ReactNode;
  /** Use a CSS-grid `1fr` / `0fr` transition that adapts to content height instead of the measured pixel height. */
  autoHeight?: boolean;
}

export interface AccordionProps<T = AccordionItem>
  extends HTMLAttributes<HTMLDivElement> {
  /** Render the default white background panel chrome around items. Defaults to `true`. */
  withBackground?: boolean;
  /** Visual variant. `'secondary'` uses the alternate header treatment with the `StarEmpty` default leading icon. */
  variant?: 'primary' | 'secondary';
  /** Items to render. Each `AccordionItem` becomes one expandable row. */
  items: T[];
  /** Called when an item with `content` is clicked. Receives the item, the click event, and the **next** expanded state. */
  onItemClick?: (props: {
    item: T;
    event: MouseEvent<HTMLDivElement>;
    isExpanded: boolean;
  }) => void;
  /** When true (default), opening an item collapses all others (single-open mode). When false, multiple items can be open simultaneously. */
  collapseAccordionsOnClick?: boolean;
  /** Extra class for the outer wrapper. */
  className?: string;
  /** Extra class applied to every item's wrapper. */
  itemClassName?: string;
  /**
   * Reserved for setting initial expansion by item id. Currently
   * unused by the implementation — initial expansion is driven by
   * `items[].expandedByDefault` instead.
   */
  defaultExpandedIds?: string[];
  /** CSS transition duration for expand/collapse animation, in ms. Defaults to `400`. */
  duration?: number;
  /** When true, items can only be expanded — clicking an open item does not collapse it. Defaults to `false`. */
  disabledCollapse?: boolean;
}

/**
 * Vertical stack of expandable rows (collapsible panels) — used to
 * group related content where only some sections are visible at a time.
 *
 * By default, opening one item collapses the others
 * (`collapseAccordionsOnClick`). Set it to `false` for an
 * "all open at once" panel-set, or `disabledCollapse` for an
 * "open-only" wizard step. Initial expansion is driven by setting
 * `expandedByDefault` on individual items.
 */
export const Accordion = <T extends AccordionItem>({
  withBackground = true,
  items,
  onItemClick,
  collapseAccordionsOnClick = true,
  className,
  itemClassName,
  defaultExpandedIds,
  variant = 'primary',
  duration = 400,
  disabledCollapse = false,
  ...rest
}: AccordionProps<T>) => {
  const [expandedItemsIdx, setExpandedItemsIdx] = useState<Set<number>>(
    () => new Set()
  );
  const itemsContentHeights = useRef(new Map());

  const handleClick = ({
    event,
    accordion,
    idx,
  }: {
    event: MouseEvent<HTMLDivElement>;
    accordion: T;
    idx: number;
  }) => {
    if (!accordion.content) {
      return;
    }

    const newSet: Set<number> = collapseAccordionsOnClick
      ? new Set()
      : new Set(expandedItemsIdx);

    if (disabledCollapse) {
      newSet.add(idx);
      setExpandedItemsIdx(newSet);
    } else {
      expandedItemsIdx.has(idx) ? newSet.delete(idx) : newSet.add(idx);
      setExpandedItemsIdx(newSet);
    }

    onItemClick?.({
      event,
      item: accordion,
      isExpanded: !expandedItemsIdx.has(idx),
    });
  };

  // Reset expansion state only when the set of `expandedByDefault` indices
  // actually changes — not on every `items` reference change
  const expandedByDefaultKey = items
    .map((item, idx) => (item.expandedByDefault ? idx : -1))
    .filter(idx => idx !== -1)
    .join(',');

  useEffect(() => {
    const exp = expandedByDefaultKey
      ? expandedByDefaultKey.split(',').map(Number)
      : [];

    setExpandedItemsIdx(new Set(exp));
  }, [expandedByDefaultKey]);

  const itemsWithIds = items.map((item, idx) => ({
    ...item,
    id: item.id ?? (performance.now() + '').replace('.', '') + idx,
  }));

  return (
    <div
      data-accordion
      data-testid="accordion"
      className={clsx(styles.accordion, className, {
        [styles.withBackground]: withBackground,
        [styles.secondary]: variant === 'secondary',
      })}
      {...rest}
    >
      {itemsWithIds.map((accordionItem, idx) => (
        <React.Fragment key={idx}>
          <div
            data-accordion-item
            data-testid="accordion-item"
            {...accordionItem.props}
            className={clsx(
              styles.accordionItem,
              expandedItemsIdx.has(idx) && 'accordion-expanded',
              itemClassName,
              accordionItem.className,
              { [styles.hideIcon]: accordionItem.hideIcon }
            )}
          >
            <div
              data-accordion-title
              onClick={event =>
                handleClick({ event, accordion: accordionItem, idx })
              }
              aria-controls={accordionItem.id}
              role="button"
              data-testid="accordion-item-button"
              className={clsx(styles.titleContainer, {
                [styles.expandable]: !!accordionItem.content,
              })}
            >
              <span
                data-accordion-title-content
                className={styles.titleContent}
              >
                {(accordionItem.titleIcon ||
                  (variant === 'secondary' &&
                    !accordionItem.hideDefaultTitleIcon)) && (
                  <Icon
                    iconName={accordionItem.titleIcon ?? 'StarEmpty'}
                    fillColor="var(--icon-primary)"
                    size={18}
                  />
                )}
                {accordionItem.title}
              </span>
              <span className={styles.rightAlignedItems}>
                {accordionItem.rightAlignedChild}
                {accordionItem?.showHasValue && !!accordionItem.value && (
                  <>{accordionItem.value}</>
                )}
                {accordionItem?.renderIcon?.(expandedItemsIdx.has(idx)) ?? (
                  <Icon
                    data-accordion-icon
                    iconName="ChevronLightRight"
                    fillColor="var(--icon-primary)"
                    size={16}
                    className={clsx(styles.iconStyled, {
                      [styles.withDefaultIcon]: !accordionItem?.renderIcon,
                      [styles.expanded]: expandedItemsIdx.has(idx),
                    })}
                  />
                )}
              </span>
            </div>
            {!!accordionItem.content && (
              <div
                data-accordion-transition
                data-testid="transition-wrapper"
                className={clsx(styles.transitionWrapper, {
                  [styles.autoHeightTransition]: accordionItem.autoHeight,
                })}
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- CSS variable `--duration` is not in `React.CSSProperties`
                style={{ '--duration': `${duration}ms` } as React.CSSProperties}
                ref={node => {
                  if (!node) {
                    return;
                  }

                  const isExpanded = expandedItemsIdx.has(idx);

                  if (accordionItem.autoHeight) {
                    node.style.height = '';
                    node.style.gridTemplateRows = isExpanded ? '1fr' : '0fr';
                  } else {
                    const height = itemsContentHeights.current.get(idx);
                    node.style.gridTemplateRows = '';
                    node.style.height = isExpanded ? `${height}px` : '0px';
                  }
                }}
              >
                <div
                  data-accordion-content
                  id={accordionItem.id}
                  className={styles.content}
                  ref={node => {
                    itemsContentHeights.current.set(
                      idx,
                      accordionItem.minHeight
                        ? accordionItem?.minHeight
                        : node?.clientHeight ?? 0
                    );
                  }}
                >
                  {accordionItem.content}
                </div>
              </div>
            )}
          </div>
          {accordionItem.footer}
        </React.Fragment>
      ))}
    </div>
  );
};
